<template>
  <div>
    <b-col md="12">
      <div>
        <b-card>
          <div>
            <!-- Data Table  -->
            <div class="d-flex justify-content-between mt-4 mb-2">
              <h2>{{ $t("common.invoices") }}</h2>
            </div>

            <!-- Bulk Selection Header -->
            <b-row
              v-if="selectedInvoices.length > 0"
              class="mb-3"
            >
              <b-col cols="12">
                <b-card class="bulk-selection-header">
                  <div
                    class="d-flex justify-content-between align-items-center flex-wrap"
                  >
                    <div class="d-flex align-items-center flex-wrap">
                      <div class="mr-3 mb-2 mb-md-0">
                        <strong>
                          {{ $t("common.selected") }}: {{ selectedInvoices.length }}
                          {{ $t("common.invoices") }}
                        </strong>
                      </div>
                      <div class="mr-3 mb-2 mb-md-0">
                        <strong>
                          {{ $t("common.total") }}:
                          <span class="text-success">
                            {{ formatPrice(selectedInvoicesTotal) }}
                          </span>
                        </strong>
                      </div>
                    </div>
                    <div class="d-flex align-items-center flex-wrap gap-2">
                      <b-button
                        v-if="
                          selectedInvoices.length > 0 &&
                            selectedInvoices.every(
                              (id) =>
                                this.invoices.data.find((inv) => inv.id === id).status ===
                                'pending'
                            )
                        "
                        variant="primary"
                        size="sm"
                        :disabled="bulkUpdating"
                        @click="showBulkApproveConfirm"
                      >
                        <feather-icon
                          icon="CheckIcon"
                          size="14"
                          class="mr-1"
                        />
                        {{ $t("common.approve") }}
                      </b-button>
                      <b-button
                        v-if="canBulkPrint"
                        variant="success"
                        size="sm"
                        :disabled="bulkUpdating"
                        @click="bulkPrintSummary"
                      >
                        <feather-icon
                          icon="PrinterIcon"
                          size="14"
                          class="mr-1"
                        />
                        {{ $t("common.print-list") }}
                      </b-button>
                      <b-button
                        v-if="canBulkPrint"
                        variant="info"
                        size="sm"
                        :disabled="bulkUpdating"
                        @click="bulkPrintDetailed"
                      >
                        <feather-icon
                          icon="FileTextIcon"
                          size="14"
                          class="mr-1"
                        />
                        {{ $t("common.print-detailed") }}
                      </b-button>
                      <b-button
                        variant="outline-secondary"
                        size="sm"
                        @click="clearSelection"
                      >
                        <feather-icon
                          icon="XIcon"
                          size="14"
                          class="mr-1"
                        />
                        {{ $t("common.clear-selection") }}
                      </b-button>
                    </div>
                  </div>
                </b-card>
              </b-col>
            </b-row>
            <b-table
              v-if="!loading"
              :items="this.invoices.data"
              responsive
              :fields="tableColumns"
              primary-key="id"
              show-empty
              empty-text="No matching results found"
              class="position-relative"
            >
              <template #head(select)>
                <b-form-checkbox
                  :checked="areAllInvoicesSelected"
                  :indeterminate="areSomeInvoicesSelected"
                  @change="toggleSelectAll"
                />
              </template>
              <template #cell(select)="data">
                <b-form-checkbox
                  :checked="isInvoiceSelected(data.item.id)"
                  @change="toggleInvoiceSelection(data.item.id, $event)"
                />
              </template>
              <template #cell(service)="data">
                <span class="text-nowrap"> Opearation </span>
              </template>
              <template #cell(amount)="data">
                <span class="text-nowrap"> {{ data.item.amount }} IQD </span>
              </template>
              <template #cell(status)="data">
                <div>
                  <ChangeInvoiceStatusDropDown
                    name="outClinicInvoice"
                    endpoint="invoices"
                    :data="data.item"
                    @onEmit="$emit('onChangeStatus')"
                  />
                </div>
              </template>
              <!-- Column: Actions -->
              <template #cell(actions)="data">
                <div class="text-nowrap d-flex">
                  <b-button
                    v-if="data.item.status == 'approved'"
                    variant="outline-primary"
                    size="sm"
                    class="mx-4"
                    @click="showOutClinicInvoice(data.item)"
                  >
                    {{ $t("reception.Print Invoice") }}
                  </b-button>
                  <b-modal
                    :id="`modal-confirm-print-${data.item.id}`"
                    :no-close-on-backdrop="true"
                    :title="$t('surgery.print-invoice')"
                    ok-only
                    :ok-title="$t('common.print')"
                    @ok="generatePdf"
                  >
                    <b-card-text>
                      <h5>{{ $t("surgery.print-invoice") }}</h5>
                      {{ $t("surgery.print-sure") }}
                    </b-card-text>
                  </b-modal>

                </div>
              </template>
            </b-table>
            <template v-if="loading">
              <div>
                <div class="d-flex justify-content-center mb-1">
                  <b-spinner label="Loading..." />
                </div>
              </div>
            </template>
            <div class="mx-2 mb-2">
              <b-row>
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-start"
                >
                  <span
                    class="text-muted"
                  >{{ $t("common.showing") }} 1 {{ $t("common.to") }} 10
                  </span>
                </b-col>
                <!-- Pagination -->
                <b-col
                  cols="12"
                  sm="6"
                  class="d-flex align-items-center justify-content-center justify-content-sm-end"
                  @click="getInvoices()"
                >
                  <b-pagination
                    v-model="page"
                    :total-rows="dataCount"
                    :per-page="take"
                    first-number
                    last-number
                    class="mb-0 mt-1 mt-sm-0"
                    prev-class="prev-item"
                    next-class="next-item"
                  >
                    <template #prev-text>
                      <feather-icon
                        icon="ChevronLeftIcon"
                        size="18"
                      />
                    </template>
                  </b-pagination>
                </b-col>
              </b-row>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Print Components -->
      <div ref="printContent">
        <OutClinicInvoice
          :patient-data="patientData"
          :data="invoice"
        />
      </div>

      <!-- Bulk Print Summary Component -->
      <InvoiceSummaryPrint
        id="BulkPrintSummaryOutClinic"
        :invoices="bulkPrintInvoicesData"
        :total-amount="bulkPrintTotal"
        invoice-type="outclinic"
      />

      <!-- Shared Invoice List PDF Component -->
      <InvoicesListPdf
        v-if="listPrintItemsOutClinic.length > 0"
        ref="outClinicInvoicesListPdf"
        :patient-name="listPrintPatientName"
        :print-date="new Date()"
        :items="listPrintItemsOutClinic"
        :title="$t('hospital.outclinic-invoices')"
        filename="outclinic-invoices-list"
        @hasStartedGeneration="isGeneratingPdf = true"
        @hasGenerated="isGeneratingPdf = false"
      />

      <!-- Loading Overlay -->
      <div v-if="isGeneratingPdf" class="pdf-loading-overlay">
        <div class="pdf-loading-content">
          <b-spinner variant="primary" label="Loading..."></b-spinner>
          <p class="mt-2">{{ $t("common.generating-pdf") || "جاري إنشاء PDF..." }}</p>
        </div>
      </div>

      <!-- Bulk Print Detailed Components -->
      <div id="BulkPrintOutClinicInvoices">
        <div
          v-for="inv in bulkPrintInvoicesData"
          :key="`bulk-outclinic-${inv.id}`"
          class="print-page-break"
        >
          <OutClinicInvoice
            :patient-data="getBulkPatientData(inv)"
            :data="inv"
          />
        </div>
      </div>

      <!-- Bulk Approve Confirmation Modal -->
      <b-modal
        id="modal-bulk-approve-confirm"
        :title="$t('common.confirm')"
        :ok-title="$t('common.approve')"
        :cancel-title="$t('common.cancel')"
        ok-variant="primary"
        cancel-variant="secondary"
        @ok="confirmBulkApprove"
        @cancel="cancelBulkApprove"
      >
        <b-card-text>
          <p style="font-size: 16px; text-align: right; direction: rtl;">
            <strong>هل أنت متأكد من الموافقة على {{ selectedInvoices.length }} فاتورة؟</strong>
          </p>
          <p style="font-size: 14px; text-align: right; direction: rtl; color: #666;">
            سيتم تغيير حالة جميع الفواتير المحددة إلى "موافق عليها"
          </p>
        </b-card-text>
      </b-modal>

      <!-- Hidden print buttons -->
      <b-button
        v-if="bulkPrintInvoicesData.length > 0"
        ref="BulkPrintSummaryRef"
        v-print="BulkPrintSummaryConfig"
        variant="outline-primary"
        size="sm"
        class="hidden"
      >
        {{ $t("common.print-summary") }}
      </b-button>

      <b-button
        v-if="bulkPrintInvoicesData.length > 0"
        ref="BulkPrintRef"
        v-print="BulkPrintConfig"
        variant="outline-primary"
        size="sm"
        class="hidden"
      >
        {{ $t("common.print-detailed") }}
      </b-button>
    </b-col>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BLink,
  BDropdown,
  BDropdownItem,
  BSpinner,
  BTooltip,
  BFormGroup,
  BFormSelect,
  BModal,
  BCardText,
  BFormCheckboxGroup,
  BFormCheckbox,
  BCardBody,
  BPagination,
  BTable,
  BBadge,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'

import ActionsButtons from '@/components/common/ActionsButtons.vue'
import DeleteModal from '@/components/modals/DeleteModal.vue'
import { getNestedProperty } from '@/utils/nestedProperty.js'

const AutoComplete = () => import('@/components/common/AutoComplete.vue')
const OutClinicInvoice = () => import('@/components/printables/OutClinicInvoice.vue')
const ChangeInvoiceStatusDropDown = () => import('@/components/invoices/ChangeInvoiceStatusDropDown.vue')
const InvoiceSummaryPrint = () => import('@/components/printables/InvoiceSummaryPrint.vue')
const InvoicesListPdf = () => import('@/components/printables/InvoicesListPdf.vue')
export default {
  components: {
    BCard,
    BRow,
    BBadge,
    BCol,
    BFormInput,
    BButton,
    BLink,
    BDropdown,
    BDropdownItem,
    BTooltip,
    vSelect,
    ToastificationContent,
    VuePhoneNumberInput,
    DeleteModal,
    ActionsButtons,
    BFormGroup,
    BFormSelect,
    BSpinner,
    BModal,
    BCardText,
    BFormCheckbox,
    BFormCheckboxGroup,
    BCardBody,
    BPagination,
    BTable,
    ChangeInvoiceStatusDropDown,
    OutClinicInvoice,
    InvoiceSummaryPrint,
    InvoicesListPdf,
  },
  props: ['invoices', 'patientData'],
  data() {
    return {
      statusOptions: [],
      tableColumns: [
        {
          key: 'select',
          label: '',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '50px' },
        },
        {
          key: 'service.title',
          label: this.$t('reception.service'),
          sortable: true,
        },
        {
          key: 'amount',
          label: this.$t('employee.amount'),
          sortable: true,
        },

        {
          key: 'created_at',
          sortable: true,
          label: this.$t('common.created-at'),
        },
        {
          key: 'invoice_no',
          sortable: true,
          label: this.$t('hospital.invoice-no'),
        },
        {
          key: 'status',
          sortable: true,
          label: 'Status',
        },
        { key: 'Actions', label: this.$t('common.actions') },
      ],
      invoiceLatestStatus: '',
      InvoiceId: '',
      invoice: '',
      isGenerating: false,
      isGeneratingPdf: false,
      currentPage: 1,
      takeOptions: [10, 25, 50, 100],
      searchQuery: '',
      sortBy: 'id',
      isSortDirDesc: true,
      page: 1,
      dataCount: 0,
      selectedInvoices: [],
      bulkUpdating: false,
      bulkPrintInvoicesData: [],
      BulkPrintConfig: {
        id: 'BulkPrintOutClinicInvoices',
      },
      BulkPrintSummaryConfig: {
        id: 'BulkPrintSummaryOutClinic',
      },
    }
  },

  computed: {
    areAllInvoicesSelected() {
      const invoicesData = this.invoices?.data || []
      return (
        invoicesData.length > 0
        && invoicesData.every(inv => this.selectedInvoices.includes(inv.id))
      )
    },
    areSomeInvoicesSelected() {
      return this.selectedInvoices.length > 0 && !this.areAllInvoicesSelected
    },
    canBulkPrint() {
      if (this.selectedInvoices.length === 0) return false
      const invoicesData = this.invoices?.data || []
      return this.selectedInvoices.every(id => {
        const invoice = invoicesData.find(inv => inv.id === id)
        return invoice && invoice.status === 'approved'
      })
    },
    selectedInvoicesTotal() {
      const invoicesData = this.invoices?.data || []
      return this.selectedInvoices.reduce((total, invoiceId) => {
        const invoice = invoicesData.find(inv => inv.id === invoiceId)
        return total + (invoice ? Number(invoice.amount) || 0 : 0)
      }, 0)
    },
    bulkPrintTotal() {
      return this.bulkPrintInvoicesData.reduce((total, invoice) => total + (Number(invoice.amount) || 0), 0)
    },

    listPrintItemsOutClinic() {
      if (!this.bulkPrintInvoicesData || this.bulkPrintInvoicesData.length === 0) {
        return [];
      }
      return this.bulkPrintInvoicesData.map(invoice => ({
        invoice_no: invoice.invoice_no,
        patient_name: getNestedProperty(invoice, 'patient_card.patient.name', 'N/A'),
        name: getNestedProperty(invoice, 'service.title', 'Out Clinic Invoice'),
        amount: invoice.amount || 0,
        discount: invoice.discount || 0,
        date: invoice.date || invoice.created_at,
        created_at: invoice.created_at,
        id: invoice.id,
      }));
    },

    listPrintPatientName() {
      if (this.patientData && this.patientData.name) {
        return this.patientData.name;
      }
      if (this.bulkPrintInvoicesData && this.bulkPrintInvoicesData.length > 0) {
        const firstInvoice = this.bulkPrintInvoicesData[0];
        return getNestedProperty(firstInvoice, 'patient_card.patient.name', 'N/A');
      }
      return 'N/A';
    },
  },

  mounted() {},
  methods: {
    getNestedProperty,
    showOutClinicInvoice(item) {
      console.log(item)
      this.invoice = item
      console.log('this.invoice', this.invoice)
      this.$bvModal.show(`modal-confirm-print-${item.id}`)
    },
    generatePdf() {
      this.isGenerating = true
      const content = this.$refs.printContent
      if (!content) {
        this.isGenerating = false
        return
      }

      // Get the innerHTML of the OutClinicInvoice component
      const invoiceElement = content.querySelector('#OutClinicInvoice')
      const htmlContent = invoiceElement ? invoiceElement.innerHTML : content.innerHTML

      // Get all styles from the document
      const styles = Array.from(document.styleSheets)
        .map(styleSheet => {
          try {
            return Array.from(styleSheet.cssRules)
              .map(rule => rule.cssText)
              .join('\n')
          } catch (e) {
            return ''
          }
        })
        .join('\n')

      const fullHtmlContent = `
<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Out Clinic Invoice</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
      padding: 12px;
      color: #111827;
      line-height: 1.4;
      direction: rtl;
    }
    #OutClinicInvoice {
      display: block !important;
      visibility: visible !important;
    }
    .print-content {
      display: block !important;
    }
    .rtl {
      direction: rtl;
    }
    .ltr {
      direction: ltr;
    }
    p.solid {
      border-style: solid;
    }
    table {
      background-color: #333;
      width: 60%;
      border-collapse: separate;
      border-spacing: 0;
      margin: 20px auto;
      border-radius: 10px;
      overflow: hidden;
      border: 3px solid #333;
    }
    th, td {
      padding: 8px;
      text-align: center;
      border-bottom: 1px solid #333;
    }
    th {
      background-color: #f2f2f2;
    }
    tr:last-child td {
      border-bottom: none;
    }
    @media print {
      body {
        padding: 10mm;
      }
      .print-content {
        display: block !important;
      }
      button {
        display: none !important;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    }
    ${styles}
  </style>
</head>
<body>
  ${htmlContent}
  <script>
    window.onload = function() {
      window.print();
    };
  <\/script>
</body>
</html>
      `

      // Open in a new tab/window and write content
      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        alert(
          this.$t('common.popup-blocked') || 'Please allow popups for this site',
        )
        this.isGenerating = false
        return
      }

      // Write content to the new window
      printWindow.document.open()
      printWindow.document.write(fullHtmlContent)
      printWindow.document.close()

      // Focus on the new window after a short delay to ensure it's loaded
      setTimeout(() => {
        printWindow.focus()
      }, 100)

      // Listen for print window close
      const checkClosed = setInterval(() => {
        if (printWindow.closed) {
          clearInterval(checkClosed)
          this.isGenerating = false
        }
      }, 500)

      // Also handle after print dialog closes
      printWindow.onbeforeunload = () => {
        clearInterval(checkClosed)
        this.isGenerating = false
      }

      // Reset generating state after a delay
      setTimeout(() => {
        this.isGenerating = false
      }, 1000)
    },
    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : '0 IQD'
    },
    // Bulk Selection Methods
    isInvoiceSelected(invoiceId) {
      return this.selectedInvoices.includes(invoiceId)
    },
    toggleInvoiceSelection(invoiceId, event) {
      if (event) {
        if (!this.selectedInvoices.includes(invoiceId)) {
          this.selectedInvoices.push(invoiceId)
        }
      } else {
        this.selectedInvoices = this.selectedInvoices.filter(
          id => id !== invoiceId,
        )
      }
    },
    clearSelection() {
      this.selectedInvoices = []
    },
    toggleSelectAll(event) {
      if (event) {
        const invoicesData = this.invoices?.data || []
        this.selectedInvoices = invoicesData.map(inv => inv.id)
      } else {
        this.selectedInvoices = []
      }
    },
    showBulkApproveConfirm() {
      this.$bvModal.show('modal-bulk-approve-confirm')
    },
    confirmBulkApprove() {
      this.bulkUpdateStatus('approved')
    },
    cancelBulkApprove() {
      // Modal will close automatically
    },
    async bulkUpdateStatus(status) {
      if (this.selectedInvoices.length === 0 || this.bulkUpdating) return

      this.bulkUpdating = true
      try {
        const invoicesPayload = this.selectedInvoices.map(id => ({
          id,
          status,
        }))

        await this.$http.post('/invoices/bulkUpdateStatus', {
          invoices: invoicesPayload,
        })

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('common.status-updated-successfully'),
          },
        })

        this.clearSelection()
        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Error bulk updating status:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text:
              error.response?.data?.message || this.$t('common.error-occurred'),
          },
        })
      } finally {
        this.bulkUpdating = false
      }
    },
    async bulkPrintSummary() {
      if (this.selectedInvoices.length === 0 || !this.canBulkPrint) return

      const invoicesData = this.invoices?.data || []
      const invoicesToPrint = invoicesData.filter(inv => this.selectedInvoices.includes(inv.id))

      if (invoicesToPrint.length === 0) return

      this.bulkPrintInvoicesData = []
      this.isGeneratingPdf = true

      try {
        const invoiceIds = invoicesToPrint.map(inv => inv.id).join(',')
        const includeString = 'service,surgery,patientCard,doctor,invoiceItems'

        const url = `/invoices?filter[id]=${invoiceIds}&include=${includeString}&take=1000`
        const response = await this.$http.get(url)

        if (response.data?.data && response.data.data.length > 0) {
          this.bulkPrintInvoicesData = response.data.data

          await this.$nextTick()
          await this.$nextTick()

          // Use the new shared PDF component
          setTimeout(() => {
            if (this.$refs.outClinicInvoicesListPdf) {
              this.$refs.outClinicInvoicesListPdf.generatePdf();
            }
          }, 100)

          // Safety timeout: ensure loading state clears even if preview is closed manually
          setTimeout(() => {
            if (this.isGeneratingPdf) {
              this.isGeneratingPdf = false
            }
          }, 15000)
        }
      } catch (error) {
        console.error('Error fetching invoices for bulk print:', error)
        this.isGeneratingPdf = false
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-occurred'),
          },
        })
      }
    },
    async bulkPrintDetailed() {
      if (this.selectedInvoices.length === 0 || !this.canBulkPrint) return

      const invoicesData = this.invoices?.data || []
      const invoicesToPrint = invoicesData.filter(inv => this.selectedInvoices.includes(inv.id))

      if (invoicesToPrint.length === 0) return

      this.bulkPrintInvoicesData = []

      try {
        const invoiceIds = invoicesToPrint.map(inv => inv.id).join(',')
        const includeString = 'service,surgery,patientCard,doctor,invoiceItems'

        const url = `/invoices?filter[id]=${invoiceIds}&include=${includeString}&take=1000`
        const response = await this.$http.get(url)

        if (response.data?.data && response.data.data.length > 0) {
          this.bulkPrintInvoicesData = response.data.data

          await this.$nextTick()
          await this.$nextTick()

          setTimeout(() => {
            if (this.$refs.BulkPrintRef) {
              this.$refs.BulkPrintRef.click()
            } else {
              window.print()
            }

            setTimeout(() => {
              this.updateBulkPrintStatus()
            }, 1000)
          }, 500)
        }
      } catch (error) {
        console.error('Error fetching invoices for bulk print:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('common.error-occurred'),
          },
        })
      }
    },
    async updateBulkPrintStatus() {
      if (this.selectedInvoices.length === 0) return

      try {
        const invoicesPayload = this.selectedInvoices.map(id => ({
          id,
          status: 'printed',
        }))

        await this.$http.post('/invoices/bulkUpdateStatus', {
          invoices: invoicesPayload,
        })

        this.clearSelection()
        this.bulkPrintInvoicesData = []
        this.$emit('onChangeStatus')
      } catch (error) {
        console.error('Error updating bulk print status:', error)
      }
    },
    getBulkPatientData(invoice) {
      if (invoice && invoice.patient_card) {
        return {
          ...invoice.patient_card,
          patient: invoice.patient_card.patient
            ? { ...invoice.patient_card.patient }
            : null,
        }
      }
      return this.patientData || {}
    },
  },
}
</script>

<style>
.hidden {
  display: none;
}

.print-page-break {
  page-break-after: always;
  display: block;
}

.pdf-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.pdf-loading-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
</style>
